/*
 * Storage-side admin surface for the OTA / Artifact-Distribution epic
 * (docs/epic-ota-artifact-distribution.md, slices S1..S5).
 *
 * Layout:
 *   1. Signer-Info banner (top, spans all tabs): is the master signer live?
 *      Which key/algo? A warn banner when disabled so operators do not
 *      upload files they think are signed but are not.
 *   2. Toolbar (artifacts/policies only) with a prominent "Neues Artefakt
 *      hochladen" button. The Rollouts tab brings its own toolbar (status
 *      filter + "Neuer Rollout") — see RolloutsTab.
 *   3. Tab strip: "Vorhandene Artefakte" (list + Rollout-Trigger), "Policies"
 *      (kind-policy editor), "Rollouts" (S4/S5 phased-rollout lifecycle).
 *   4. Upload dialog: modal-hosted version of the former inline form.
 */

import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';
import { RolloutsTab } from './RolloutsPage';
import { sessionSignal } from '../auth/session';
import type {
  ArtifactDto,
  ArtifactKind,
  ArtifactKindPolicyDto,
  ArtifactSignerInfoResponse,
  ListArtifactsResponse,
  ListArtifactKindPoliciesResponse,
  ListRolloutReportsResponse,
  RolloutReportDto,
  RolloutTargetStatus,
  TriggerSingleClientRolloutRequest,
  TriggerSingleClientRolloutResponse,
  UpdateArtifactKindPolicyRequest,
} from '../api/types';
import './ArtifactsPage.css';

// Local button wrapper mirroring the same one on TypicalsPage/IdentityPage —
// the shared Button component's Props alias drops `disabled`/`type` at
// compile time, this wrapper reinstates them.
type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
interface BtnProps {
  variant?: BtnVariant;
  size?: 'sm' | 'md';
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  children?: ComponentChildren;
}
function Btn({ variant = 'secondary', size = 'md', onClick, disabled, type = 'button', title, children }: BtnProps) {
  const classes = ['hv-btn', `hv-btn--${variant}`, `hv-btn--${size}`, disabled ? 'hv-btn--disabled' : '']
    .filter(Boolean).join(' ');
  return <button type={type} class={classes} onClick={onClick} disabled={disabled} title={title}>{children}</button>;
}

// Jeder modellierte Kind, in Enum-Reihenfolge. S6: die beiden
// Client-Binary-Kinds fehlten hier, obwohl die Apply-Pfade am Linux-Client
// existieren und die Policies geseedet sind — man konnte also serverseitig
// alles ausrollen, was man über die UI nicht hochladen konnte.
//
// Was davon im Dropdown landet, entscheidet zusätzlich die Policy: J.16
// blendet über `hideFromRolloutUi` aus, was noch nicht erprobt ist. Siehe
// `selectableKinds`.
const KINDS: ArtifactKind[] = [
  'AndroidApk',
  'AndroidSystemOta',
  'ConfigFile',
  'CertBundle',
  'ClimatixUcf',
  'ClientLinuxArm64',
  'ClientLinuxX64',
  'GenericBlob',
];

/**
 * J.16: Kinds fuer die Kind-Auswahl, gefiltert nach `hideFromRolloutUi`.
 *
 * Das ist ein Sicherheitsnetz gegen einen versehentlichen Klick, kein
 * Rechtekonzept — wer den Kind wirklich braucht, ruft den Endpoint direkt per
 * API, und das ist so gewollt. Der Filter faengt den muede-geklickt-Fall:
 * ein fehlgeschlagenes System-Image macht ein Panel unbrickbar, und ein
 * Praxistest auf einem Zweitpanel ist nicht gelaufen.
 *
 * Ohne geladene Policies bleibt die Liste ungefiltert. Absichtlich: ein
 * fehlgeschlagener Policy-Request darf den Upload-Dialog nicht leeren, und
 * die Serverseite lehnt ohnehin ab, was sie ablehnen muss.
 */
function selectableKinds(policies: ArtifactKindPolicyDto[] | null): ArtifactKind[] {
  if (!policies || policies.length === 0) return KINDS;
  const hidden = new Set(policies.filter((p) => p.hideFromRolloutUi).map((p) => p.kind));
  return KINDS.filter((k) => !hidden.has(k));
}

/** Human-readable name for a kind. Kept UI-only — the wire value stays the
 *  enum name so a schema change on the server never silently shifts labels. */
function formatKind(kind: ArtifactKind): string {
  switch (kind) {
    case 'AndroidApk':       return 'Android APK';
    case 'AndroidSystemOta': return 'Android System OTA';
    case 'ConfigFile':       return 'Config-Datei';
    case 'CertBundle':       return 'Zertifikatspaket';
    case 'ClimatixUcf':      return 'Climatix .ucf';
    case 'ClientLinuxArm64': return 'Linux-Client arm64 (.tar.gz)';
    case 'ClientLinuxX64':   return 'Linux-Client x64 (.tar.gz)';
    case 'GenericBlob':      return 'Generic Blob';
    default:                 return kind;
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

type ArtifactsTab = 'artifacts' | 'policies' | 'rollouts';

export function ArtifactsPage() {
  const [refreshTick, setRefreshTick] = useState(0);
  const [tab, setTab] = useState<ArtifactsTab>('artifacts');
  const [uploadOpen, setUploadOpen] = useState(false);

  const signer = useAsync(
    (signal) => api.get<ArtifactSignerInfoResponse>('/api/artifacts/signer-info', signal),
    [],
  );
  const list = useAsync(
    (signal) => api.get<ListArtifactsResponse>('/api/artifacts', signal),
    [refreshTick],
  );
  const policies = useAsync(
    (signal) => api.get<ListArtifactKindPoliciesResponse>('/api/artifact-kind-policies', signal),
    [refreshTick],
  );

  const refresh = () => setRefreshTick((t) => t + 1);

  return (
    <AppShell title="Artefakte & OTA">
      <div class="hv-artifacts">
        {/* Signer-Info steht ganz oben, weil sie beide Tabs betrifft: ein
            deaktivierter Signer blockt sowohl Uploads als auch Policy-Kinds
            mit requiresSignature=true. */}
        <SignerBanner signer={signer} />

        {/* Toolbar only makes sense for artifacts/policies. The Rollouts tab
            has its own toolbar (status filter + "Neuer Rollout") because a
            page-level "hochladen" button on the rollout list would be a
            wrong-context affordance. */}
        {tab !== 'rollouts' && (
          <div class="hv-artifacts__toolbar">
            <div class="hv-artifacts__toolbar-hint">
              {tab === 'artifacts'
                ? 'Ausrollen an Feld-Clients: pro Artefakt-Zeile "Rollout…".'
                : 'Änderungen wirken sofort auf neue Uploads und Rollouts.'}
            </div>
            {/* J.12: Broadcast-Impuls fuer SA-Rotation. Getrennt vom Upload-Button
                damit der Hebel als bewusst separater Ops-Trigger wahrgenommen
                wird — nicht als Nachbar von "Neues Artefakt hochladen". */}
            <BroadcastReissueSaButton />
            <Btn variant="primary" onClick={() => setUploadOpen(true)}>
              Neues Artefakt hochladen
            </Btn>
          </div>
        )}

        <div class="hv-artifacts__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'artifacts'}
            class={`hv-artifacts__tab${tab === 'artifacts' ? ' hv-artifacts__tab--active' : ''}`}
            onClick={() => setTab('artifacts')}
          >
            Vorhandene Artefakte
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'policies'}
            class={`hv-artifacts__tab${tab === 'policies' ? ' hv-artifacts__tab--active' : ''}`}
            onClick={() => setTab('policies')}
          >
            Policies
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'rollouts'}
            class={`hv-artifacts__tab${tab === 'rollouts' ? ' hv-artifacts__tab--active' : ''}`}
            onClick={() => setTab('rollouts')}
          >
            Rollouts
          </button>
        </div>

        {tab === 'artifacts' && (
          <section class="hv-artifacts__section" role="tabpanel">
            <ArtifactList list={list} policies={policies} onChanged={refresh} />
          </section>
        )}
        {tab === 'policies' && (
          <section class="hv-artifacts__section" role="tabpanel">
            <p class="hv-artifacts__section-hint">
              Definieren die Ausroll-Regeln pro Artefakttyp (Anzahl Approvals,
              ob Tenants selbst hochladen dürfen, ob Signatur Pflicht ist, wie
              lange der Client nach dem Apply beobachtet bevor er den Erfolg
              meldet).
            </p>
            <PolicyTable policies={policies} onSaved={refresh} />
          </section>
        )}
        {tab === 'rollouts' && (
          <section class="hv-artifacts__section" role="tabpanel">
            <RolloutsTab />
          </section>
        )}

        {uploadOpen && (
          <UploadModal
            policies={policies.status === 'ok' ? policies.data.policies : null}
            onClose={() => setUploadOpen(false)}
            onUploaded={() => {
              refresh();
              setUploadOpen(false);
            }}
          />
        )}
      </div>
    </AppShell>
  );
}

// --- Signer banner ---------------------------------------------------------

function SignerBanner({ signer }: { signer: ReturnType<typeof useAsync<ArtifactSignerInfoResponse>> }) {
  if (signer.status === 'loading') {
    return <div class="hv-artifacts__banner"><span>Signer-Status wird geladen…</span></div>;
  }
  if (signer.status === 'error') {
    return (
      <div class="hv-artifacts__banner hv-artifacts__banner--warn">
        <span class="hv-artifacts__banner-dot" />
        <span class="hv-artifacts__banner-text">Signer-Status konnte nicht abgefragt werden.</span>
      </div>
    );
  }
  const info = signer.data;
  if (!info.enabled) {
    return (
      <div class="hv-artifacts__banner hv-artifacts__banner--warn">
        <span class="hv-artifacts__banner-dot" />
        <span class="hv-artifacts__banner-text">
          <strong>Master-Signer ist deaktiviert.</strong> Uploads für Kinds mit
          <code> requiresSignature=true</code> antworten mit 503 bis
          <code> Artifacts:MasterSigner:PrivateKeyPem </code>
          im Vault gesetzt ist. Bis dahin lässt sich nur der Metadaten-Pfad testen.
        </span>
      </div>
    );
  }
  return (
    <div class="hv-artifacts__banner">
      <span class="hv-artifacts__banner-dot" />
      <span class="hv-artifacts__banner-text">
        Master-Signer aktiv: <strong>{info.signer}</strong>
        <span class="hv-artifacts__banner-key">{info.algorithm}</span>
      </span>
    </div>
  );
}

// --- Broadcast SA-Reissue button (concept J.12) ---------------------------
// One-shot button that publishes an MQTT impulse on
// hvacloud/v1/clients/broadcast/reissue-sa. Every online field-client picks
// it up and calls the mTLS reissue endpoint independently — the operator
// only clicks once after a Keycloak SA rotation. The button is intentionally
// non-primary because it's a rare-use ops lever, not a daily action.

function BroadcastReissueSaButton() {
  const [busy, setBusy] = useState(false);
  const trigger = async () => {
    // Confirm-before-fire so an accidental click doesn't wake up the whole
    // fleet. Not a modal — a browser confirm() is enough for a rare-use
    // ops trigger.
    // eslint-disable-next-line no-alert
    const ok = confirm(
      'Broadcast an alle Feld-Clients: neu ausgestellte Service-Account-Credentials abholen?\n\n'
        + 'Der Impuls geht an jeden aktuell online-Client. Der eigentliche Reissue-Aufruf pro Client'
        + ' erfolgt individuell gegen den mTLS-Endpoint.',
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.post<{ topic: string }>('/api/enrollment/broadcast-sa-reissue');
      showToast({
        tone: 'success',
        title: 'Broadcast gesendet',
        message: `Topic: ${res.topic}. Clients rollen den Endpoint selbständig an.`,
        autoHideAfterMs: 5000,
      });
    } catch (err) {
      const message = err instanceof ApiError
        ? `HTTP ${err.status}: ${err.message}`
        : (err as Error)?.message ?? 'Unbekannter Fehler.';
      showToast({
        tone: 'error',
        title: 'Broadcast fehlgeschlagen',
        message,
        autoHideAfterMs: 8000,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Btn variant="secondary" size="md" onClick={trigger} disabled={busy}>
      {busy ? 'Sende Broadcast…' : 'Broadcast SA-Reissue nach Rotation'}
    </Btn>
  );
}

// --- Upload modal ----------------------------------------------------------
// Modal-hosted variant of the previous inline UploadPanel. Reuses the same
// modal chrome as RolloutModal (backdrop click + close-button) so the two
// dialogs feel like siblings, not like two disconnected components. Closes
// on successful upload, keeps state on error so the operator can fix the
// input without retyping everything.

function UploadModal({
  policies,
  onClose,
  onUploaded,
}: {
  policies: ArtifactKindPolicyDto[] | null;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const kinds = selectableKinds(policies);
  const [file, setFile] = useState<File | null>(null);
  // Startwert aus der gefilterten Liste, nicht hart 'AndroidApk': sonst
  // stuende im Dropdown ein Kind, den J.16 gerade ausgeblendet hat.
  const [kind, setKind] = useState<ArtifactKind>(kinds[0] ?? 'GenericBlob');
  const [version, setVersion] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const disabled = submitting || !file || !version.trim();

  const doUpload = async (e: Event) => {
    e.preventDefault();
    if (!file || !version.trim()) return;
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    form.append('version', version.trim());
    if (displayName.trim()) form.append('displayName', displayName.trim());
    if (description.trim()) form.append('description', description.trim());

    setSubmitting(true);
    try {
      // Direct fetch — the shared client.ts assumes JSON body, multipart
      // needs the FormData path with credentials + no Content-Type header
      // (the browser sets the multipart boundary itself).
      const res = await fetch('/api/artifacts', {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        let msg = `Upload fehlgeschlagen (HTTP ${res.status}).`;
        try {
          const body = await res.json();
          if (body?.error) msg = body.error;
          else if (body?.title) msg = `${body.title}${body.detail ? ', ' + body.detail : ''}`;
        } catch { /* ignore */ }
        showToast({ tone: 'error', title: 'Upload fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
        return;
      }
      const created: ArtifactDto = await res.json();
      showToast({
        tone: 'success',
        title: `Artefakt gespeichert`,
        message: `${formatKind(created.kind)} · ${created.version} · sha256 ${created.contentSha256.slice(0, 12)}…`,
        autoHideAfterMs: 4000,
      });
      onUploaded();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div class="hv-artifacts__modal-backdrop" onClick={onClose}>
      <div class="hv-artifacts__modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-artifacts__modal-head">
          <h3>Neues Artefakt hochladen</h3>
          <Btn variant="ghost" size="sm" onClick={onClose}>×</Btn>
        </div>
        <form class="hv-artifacts__modal-body" onSubmit={doUpload}>
          <p class="hv-artifacts__section-hint">
            APK, Config, Cert-Bundle, Climatix-.ucf oder Linux-Client-Tarball.
            Der Server prüft, dass die Datei zum gewählten Kind passt. Wird
            beim Upload signiert und ist über
            <code>GET /api/artifacts/&#123;id&#125;/content</code> abrufbar.
          </p>
          <div class="hv-artifacts__upload">
            <div class="hv-artifacts__upload-field hv-artifacts__upload-field--full">
              <label for="hv-artifacts-file">Datei</label>
              <input
                id="hv-artifacts-file"
                type="file"
                onChange={(e) => setFile((e.currentTarget as HTMLInputElement).files?.[0] ?? null)}
              />
            </div>
            <div class="hv-artifacts__upload-field">
              <label for="hv-artifacts-kind">Kind</label>
              <select id="hv-artifacts-kind" value={kind}
                      onChange={(e) => setKind((e.currentTarget as HTMLSelectElement).value as ArtifactKind)}>
                {kinds.map((k) => <option key={k} value={k}>{formatKind(k)}</option>)}
              </select>
            </div>
            <div class="hv-artifacts__upload-field">
              <label for="hv-artifacts-version">Version</label>
              <input
                id="hv-artifacts-version"
                type="text"
                value={version}
                placeholder="1.4.0"
                onInput={(e) => setVersion((e.currentTarget as HTMLInputElement).value)}
              />
            </div>
            <div class="hv-artifacts__upload-field hv-artifacts__upload-field--full">
              <label for="hv-artifacts-name">Anzeigename (optional)</label>
              <input
                id="hv-artifacts-name"
                type="text"
                value={displayName}
                placeholder="Panel-Agent 1.4.0"
                onInput={(e) => setDisplayName((e.currentTarget as HTMLInputElement).value)}
              />
            </div>
            <div class="hv-artifacts__upload-field hv-artifacts__upload-field--full">
              <label for="hv-artifacts-desc">Beschreibung (optional, Release-Notes)</label>
              <textarea
                id="hv-artifacts-desc"
                rows={2}
                value={description}
                onInput={(e) => setDescription((e.currentTarget as HTMLTextAreaElement).value)}
              />
            </div>
          </div>
          <div class="hv-artifacts__modal-actions">
            <Btn variant="ghost" onClick={onClose} disabled={submitting}>Abbrechen</Btn>
            <Btn variant="primary" type="submit" disabled={disabled}>
              {submitting ? 'Lade hoch…' : 'Hochladen'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Artifact list ---------------------------------------------------------

function ArtifactList({
  list,
  policies,
  onChanged,
}: {
  list: ReturnType<typeof useAsync<ListArtifactsResponse>>;
  policies: ReturnType<typeof useAsync<ListArtifactKindPoliciesResponse>>;
  onChanged?: () => void;
}) {
  const [rolloutTarget, setRolloutTarget] = useState<ArtifactDto | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  // Spiegelt die Server-Regel in DeleteArtifactEndpoint: Loeschen nur mit
  // plants.admin oder artifacts.admin. Der Knopf bleibt sichtbar, damit
  // klar ist, dass es die Funktion gibt, nur eben nicht fuer jeden.
  const permissions = sessionSignal.value.user?.permissions ?? [];
  const canDelete = permissions.includes('plants.admin') || permissions.includes('artifacts.admin');

  const deleteArtifact = async (a: ArtifactDto) => {
    if (!window.confirm(
      `Artefakt "${a.displayName}" v${a.version} wirklich löschen?\n\n`
      + `Der Content-Blob (${formatSize(a.contentSizeBytes)}) wird hart aus der Datenbank entfernt. `
      + `Bereits laufende Rollouts verhindern das Löschen.`)) return;
    setDeleting(a.id);
    try {
      await api.delete<void>(`/api/artifacts/${a.id}`);
      showToast({ tone: 'success', title: 'Artefakt gelöscht', message: `${a.displayName} v${a.version}`, autoHideAfterMs: 3000 });
      onChanged?.();
    } catch (e) {
      const msg = e instanceof ApiError
        ? ((e.body as { error?: string })?.error ?? `Fehler ${e.status}`)
        : (e as Error).message;
      showToast({ tone: 'error', title: 'Löschen fehlgeschlagen', message: msg, autoHideAfterMs: 8000 });
    } finally {
      setDeleting(null);
    }
  };

  if (list.status === 'loading') return <p class="hv-artifacts__empty">Lade Artefakte…</p>;
  if (list.status === 'error') return <p class="hv-artifacts__empty">Konnte Artefakte nicht laden.</p>;
  if (list.data.artifacts.length === 0) {
    return <p class="hv-artifacts__empty">Noch keine Artefakte in diesem Tenant.</p>;
  }

  const policyForKind = (kind: ArtifactKind): ArtifactKindPolicyDto | null =>
    policies.status === 'ok'
      ? policies.data.policies.find((p) => p.kind === kind) ?? null
      : null;
  return (
    <>
      <table class="hv-artifacts__table">
        <thead>
          <tr>
            <th>Kind</th>
            <th>Anzeigename</th>
            <th>Version</th>
            <th>Größe</th>
            <th>SHA-256</th>
            <th>Signaturen</th>
            <th>Erzeugt</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>
          {list.data.artifacts.map((a) => (
            <tr key={a.id}>
              <td>{formatKind(a.kind)}</td>
              <td>{a.displayName}</td>
              <td>{a.version}</td>
              <td>{formatSize(a.contentSizeBytes)}</td>
              <td class="hv-artifacts__mono" title={a.contentSha256}>{a.contentSha256.slice(0, 12)}…</td>
              <td>
                {a.signatures.length === 0
                  ? <span class="hv-artifacts__badge hv-artifacts__badge--warn">unsigniert</span>
                  : <span class="hv-artifacts__badge">{a.signatures.length}× {a.signatures[0].algo}</span>}
              </td>
              <td>{formatDate(a.createdAt)}</td>
              <td class="hv-artifacts__actions">
                <a href={`/api/artifacts/${a.id}/content`} class="hv-btn hv-btn--ghost hv-btn--sm" download>
                  Download
                </a>
                <Btn variant="secondary" size="sm" onClick={() => setRolloutTarget(a)}>
                  Rollout…
                </Btn>
                <Btn variant="danger" size="sm"
                     onClick={() => deleteArtifact(a)}
                     disabled={!canDelete || deleting === a.id}
                     title={canDelete ? undefined : 'Löschen braucht das Recht plants.admin oder artifacts.admin.'}>
                  {deleting === a.id ? 'Lösche…' : 'Löschen'}
                </Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rolloutTarget && (
        <RolloutModal
          artifact={rolloutTarget}
          policy={policyForKind(rolloutTarget.kind)}
          onClose={() => setRolloutTarget(null)}
        />
      )}
    </>
  );
}

// --- Rollout modal ---------------------------------------------------------
// Two states: 'form' (clientId input + trigger) and 'reports' (live-polling
// report table). Modal stays open until the operator dismisses it — a
// terminal status does not auto-close because they will typically want to
// read the safety-time gate, or copy the rolloutId for a bug report.

function RolloutModal({
  artifact,
  policy,
  onClose,
}: {
  artifact: ArtifactDto;
  policy: ArtifactKindPolicyDto | null;
  onClose: () => void;
}) {
  const [clientId, setClientId] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [rolloutId, setRolloutId] = useState<string | null>(null);
  const [reports, setReports] = useState<RolloutReportDto[]>([]);
  const pollHandle = useRef<number | null>(null);

  const trigger = async (e: Event) => {
    e.preventDefault();
    // Paste-tolerant: strip surrounding whitespace + accept braces (`{guid}`).
    const cleaned = clientId.trim().replace(/^[{]|[}]$/g, '');
    if (!isGuid(cleaned)) {
      showToast({ tone: 'warn', title: 'ClientId muss ein Guid sein', autoHideAfterMs: 4000 });
      return;
    }
    setTriggering(true);
    try {
      const body: TriggerSingleClientRolloutRequest = { clientId: cleaned };
      const res = await api.post<TriggerSingleClientRolloutResponse>(
        `/api/artifacts/${artifact.id}/rollouts/single-client`, body);
      setRolloutId(res.rolloutId);
      showToast({ tone: 'success', title: 'Rollout ausgelöst', message: `RolloutId ${res.rolloutId.slice(0, 8)}…`, autoHideAfterMs: 3000 });
    } catch (err) {
      const msg = err instanceof ApiError
        ? String((err.body as { detail?: string; error?: string })?.detail
                 ?? (err.body as { error?: string })?.error
                 ?? err.message)
        : String(err);
      showToast({ tone: 'error', title: 'Rollout fehlgeschlagen', message: msg, autoHideAfterMs: 6000 });
    } finally {
      setTriggering(false);
    }
  };

  // Report polling. Every 2s while modal is open + a rolloutId is present.
  // Stops once every report row is terminal (Applied/Failed/RejectedSignatureInvalid).
  useEffect(() => {
    if (!rolloutId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get<ListRolloutReportsResponse>(`/api/rollouts/${rolloutId}/reports`);
        if (cancelled) return;
        setReports(res.reports);
      } catch { /* leave last report set visible */ }
    };
    void tick();
    pollHandle.current = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (pollHandle.current !== null) {
        window.clearInterval(pollHandle.current);
        pollHandle.current = null;
      }
    };
  }, [rolloutId]);

  // Auto-stop polling once every report is terminal.
  useEffect(() => {
    if (reports.length === 0) return;
    const allTerminal = reports.every((r) => isTerminal(r.status));
    if (allTerminal && pollHandle.current !== null) {
      window.clearInterval(pollHandle.current);
      pollHandle.current = null;
    }
  }, [reports]);

  return (
    <div class="hv-artifacts__modal-backdrop" onClick={onClose}>
      <div class="hv-artifacts__modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-artifacts__modal-head">
          <h3>Rollout: {artifact.displayName} ({artifact.version})</h3>
          <Btn variant="ghost" size="sm" onClick={onClose}>×</Btn>
        </div>
        {!rolloutId ? (
          <form class="hv-artifacts__modal-body" onSubmit={trigger}>
            <p class="hv-artifacts__section-hint">
              Löst einen Single-Client-Rollout aus. Server publisht auf
              <code> hvacloud/v1/clients/&#123;clientId&#125;/rollout/next</code>. Der
              Client verifiziert sha256 + Signatur, wendet an, meldet den
              Fortschritt zurück. Kein Multi-Client-Bundle in S2 — das kommt
              mit S4.
            </p>
            <div class="hv-artifacts__upload-field hv-artifacts__upload-field--full">
              <label for="hv-rollout-client">Ziel-ClientId</label>
              <input
                id="hv-rollout-client"
                type="text"
                value={clientId}
                placeholder="00000000-0000-0000-0000-000000000000"
                onInput={(e) => setClientId((e.currentTarget as HTMLInputElement).value)}
              />
            </div>
            <div class="hv-artifacts__modal-actions">
              <Btn variant="ghost" onClick={onClose}>Abbrechen</Btn>
              <Btn variant="primary" type="submit" disabled={triggering || !isGuid(clientId.trim())}>
                {triggering ? 'Sende…' : 'Rollout senden'}
              </Btn>
            </div>
          </form>
        ) : (
          <ReportsView
            rolloutId={rolloutId}
            reports={reports}
            policy={policy}
            polling={pollHandle.current !== null}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function ReportsView({
  rolloutId,
  reports,
  policy,
  polling,
  onClose,
}: {
  rolloutId: string;
  reports: RolloutReportDto[];
  policy: ArtifactKindPolicyDto | null;
  polling: boolean;
  onClose: () => void;
}) {
  const latest = reports[0]; // reports come newest-first from the server
  const allTerminal = reports.length > 0 && reports.every((r) => isTerminal(r.status));
  const anyRejected = reports.some(
    (r) => r.status === 'Failed' || r.status === 'RejectedSignatureInvalid',
  );

  const copyRollout = async () => {
    try {
      await navigator.clipboard.writeText(rolloutId);
      showToast({ tone: 'success', title: 'RolloutId kopiert', autoHideAfterMs: 1500 });
    } catch {
      showToast({ tone: 'warn', title: 'Kopieren nicht möglich', autoHideAfterMs: 2500 });
    }
  };

  return (
    <div class="hv-artifacts__modal-body">
      <div class="hv-artifacts__rollout-header">
        <div class="hv-artifacts__rollout-id">
          <span class="hv-artifacts__section-hint">RolloutId</span>
          <code class="hv-artifacts__mono" title={rolloutId}>{rolloutId}</code>
          <Btn variant="ghost" size="sm" onClick={copyRollout}>Kopieren</Btn>
        </div>
        {latest && (
          <div class="hv-artifacts__rollout-status">
            <span class="hv-artifacts__section-hint">Aktueller Stand</span>
            <StatusBadge status={latest.status} />
          </div>
        )}
      </div>

      {policy && (
        <p class="hv-artifacts__section-hint">
          Wartezeit dieses Kinds: <strong>{policy.defaultPhaseSafetySeconds}s</strong> ab
          Prozessstart des Clients, bevor <code>Applied</code> gemeldet wird
          (verhindert dass ein Update, das direkt nach dem Reboot crasht, als
          Erfolg gilt).
        </p>
      )}
      {allTerminal && !anyRejected && (
        <p class="hv-artifacts__banner hv-artifacts__banner--ok">
          <span class="hv-artifacts__banner-dot" />
          <span class="hv-artifacts__banner-text">Rollout abgeschlossen, alle Reports terminal.</span>
        </p>
      )}
      {anyRejected && (
        <p class="hv-artifacts__banner hv-artifacts__banner--warn">
          <span class="hv-artifacts__banner-dot" />
          <span class="hv-artifacts__banner-text">
            Rollout enthält Fehlerreports. Details in der Tabelle.
          </span>
        </p>
      )}

      <table class="hv-artifacts__table">
        <thead>
          <tr>
            <th>Zeit</th>
            <th>Status</th>
            <th>Fehler</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.id}>
              <td>{formatDate(r.reportedAt)}</td>
              <td><StatusBadge status={r.status} /></td>
              <td class="hv-artifacts__mono">{r.errorMessage ?? ''}</td>
            </tr>
          ))}
          {reports.length === 0 && (
            <tr>
              <td colSpan={3} class="hv-artifacts__empty">
                {polling ? 'Warte auf Reports…' : 'Keine Reports.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div class="hv-artifacts__modal-actions">
        <Btn variant="primary" onClick={onClose}>Schließen</Btn>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: RolloutTargetStatus }) {
  const tone = statusTone(status);
  return <span class={`hv-artifacts__badge hv-artifacts__badge--${tone}`}>{status}</span>;
}

function statusTone(status: RolloutTargetStatus): 'ok' | 'warn' | 'err' | 'neutral' {
  switch (status) {
    case 'Applied':                  return 'ok';
    case 'Failed':                   return 'err';
    case 'RejectedSignatureInvalid': return 'err';
    case 'Queued':                   return 'neutral';
    default:                         return 'warn';
  }
}

function isTerminal(status: RolloutTargetStatus): boolean {
  return status === 'Applied' || status === 'Failed' || status === 'RejectedSignatureInvalid';
}

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
function isGuid(s: string): boolean { return GUID_RE.test(s); }

// --- Policy table ----------------------------------------------------------

function PolicyTable({
  policies,
  onSaved,
}: {
  policies: ReturnType<typeof useAsync<ListArtifactKindPoliciesResponse>>;
  onSaved: () => void;
}) {
  if (policies.status === 'loading') return <p class="hv-artifacts__empty">Lade Policies…</p>;
  if (policies.status === 'error') return <p class="hv-artifacts__empty">Konnte Policies nicht laden.</p>;
  return (
    <table class="hv-artifacts__table">
      <thead>
        <tr>
          <th>Kind</th>
          <th>Approvals</th>
          <th>Tenant-Upload</th>
          <th>Signatur Pflicht</th>
          <th>Safety-Time (s)</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {policies.data.policies.map((p) => (
          <PolicyRow key={p.kind} policy={p} onSaved={onSaved} />
        ))}
      </tbody>
    </table>
  );
}

function PolicyRow({ policy, onSaved }: { policy: ArtifactKindPolicyDto; onSaved: () => void }) {
  const [approvals, setApprovals] = useState(policy.requiredApprovals);
  const [allowTenant, setAllowTenant] = useState(policy.allowTenantUpload);
  const [requiresSig, setRequiresSig] = useState(policy.requiresSignature);
  const [safetySeconds, setSafetySeconds] = useState(policy.defaultPhaseSafetySeconds);
  const [saving, setSaving] = useState(false);

  const dirty =
    approvals !== policy.requiredApprovals ||
    allowTenant !== policy.allowTenantUpload ||
    requiresSig !== policy.requiresSignature ||
    safetySeconds !== policy.defaultPhaseSafetySeconds;

  const save = async () => {
    setSaving(true);
    try {
      const body: UpdateArtifactKindPolicyRequest = {
        requiredApprovals: approvals,
        allowTenantUpload: allowTenant,
        requiresSignature: requiresSig,
        defaultPhaseSafetySeconds: safetySeconds,
      };
      await api.patch<ArtifactKindPolicyDto>(`/api/artifact-kind-policies/${policy.kind}`, body);
      showToast({ tone: 'success', title: `Policy gespeichert: ${formatKind(policy.kind)}`, autoHideAfterMs: 2500 });
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? String((err.body as { error?: string })?.error ?? err.message) : String(err);
      showToast({ tone: 'error', title: 'Policy konnte nicht gespeichert werden', message: msg, autoHideAfterMs: 5000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td>
        {formatKind(policy.kind)}
        {/* J.16: sagt an der Stelle, wo Ops die Policies liest, warum der
            Kind im Upload-Dropdown fehlt. Ohne den Hinweis liest sich das
            Ausblenden als Bug. */}
        {policy.hideFromRolloutUi && (
          <>
            {' '}
            <span title="J.16: aus der Kind-Auswahl ausgeblendet, bis ein Praxistest auf einem Zweitpanel gelaufen ist. Kein Rechtekonzept — per API weiter triggerbar.">
              (nicht in der Auswahl)
            </span>
          </>
        )}
      </td>
      <td>
        <select
          class="hv-artifacts__policy-input"
          value={String(approvals)}
          onChange={(e) => setApprovals(Number((e.currentTarget as HTMLSelectElement).value))}
        >
          <option value="1">1</option>
          <option value="2">2</option>
        </select>
      </td>
      <td>
        <input
          type="checkbox"
          class="hv-artifacts__policy-checkbox"
          checked={allowTenant}
          onChange={(e) => setAllowTenant((e.currentTarget as HTMLInputElement).checked)}
        />
      </td>
      <td>
        <input
          type="checkbox"
          class="hv-artifacts__policy-checkbox"
          checked={requiresSig}
          onChange={(e) => setRequiresSig((e.currentTarget as HTMLInputElement).checked)}
        />
      </td>
      <td>
        <input
          type="number"
          min={0}
          class="hv-artifacts__policy-input"
          value={safetySeconds}
          onInput={(e) => setSafetySeconds(Number((e.currentTarget as HTMLInputElement).value))}
        />
      </td>
      <td>
        <Btn variant="primary" size="sm" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Speichern…' : 'Speichern'}
        </Btn>
      </td>
    </tr>
  );
}
